// Studio dashboard shell — sidebar nav + top bar + content area.

import { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { T } from '../theme';
import { Pegasus, DreamlinkLockup } from './DreamlinkMark';

interface NavItem {
  to: string;
  label: string;
  letter: string;
}

// All operator nav lives under /operator/* (the root `/` is now the public
// landing). Codex 2026-05-12.
// Trimmed 2026-05-27 to the operator's working set (Kevin). Operations,
// Relationships, Audit, Agents, and Verification were unused/placeholder tabs;
// their routes still resolve by URL, they're just no longer surfaced in the
// nav. Settings kept for account + sign-out.
const NAV: NavItem[] = [
  { to: '/operator', label: 'Dashboard', letter: 'i' },
  { to: '/operator/brands', label: 'Brand review', letter: 'ii' },
  { to: '/operator/campaigns', label: 'Campaigns', letter: 'iii' },
  { to: '/operator/distribution', label: 'Clips', letter: 'iv' },
  { to: '/operator/settings', label: 'Settings', letter: 'v' },
];

/** Dark system bar — the Dreamlink brand signature, shared with the brand portal. */
function SystemBar() {
  return (
    <div style={layoutStyles.systemBar}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
        <Pegasus size={14} color="#F5F2EE" />
        <b style={{ color: '#F5F2EE', fontWeight: 500, letterSpacing: '0.14em' }}>DREAMLINK</b>
        <span style={{ opacity: 0.5 }}>STUDIO</span>
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={layoutStyles.systemDot} />
        OPERATOR
      </span>
    </div>
  );
}

export default function Layout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const activeItem = [...NAV].reverse().find((n) =>
    n.to === '/operator' ? location.pathname === '/operator' : location.pathname.startsWith(n.to)
  );

  return (
    <div style={layoutStyles.shell}>
      <SystemBar />
      <div style={layoutStyles.root}>
        {/* Sidebar */}
        <aside style={layoutStyles.sidebar}>
        <div style={layoutStyles.brand}>
          <DreamlinkLockup markSize={22} color={T.ink} suffix="Studio" fontSize={15} />
        </div>

        <nav style={layoutStyles.nav}>
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              style={({ isActive }) => ({
                ...layoutStyles.navLink,
                ...(isActive ? layoutStyles.navLinkActive : {}),
              })}
            >
              <span style={layoutStyles.navLetter}>{item.letter}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div style={layoutStyles.sidebarFoot}>
          <div style={layoutStyles.foot}>
            <div>v 0.1.0 · scaffold</div>
            <div style={{ marginTop: 4 }}>studio.dreamlink.global</div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main style={layoutStyles.main}>
        <header style={layoutStyles.topbar}>
          <div style={layoutStyles.eyebrow}>
            {activeItem?.letter} · {activeItem?.label}
          </div>
          <div style={layoutStyles.topbarRight}>
            <span style={layoutStyles.opStatus}>● live</span>
          </div>
        </header>

        <section style={layoutStyles.content}>
          {children}
        </section>
      </main>
      </div>
    </div>
  );
}

const layoutStyles: Record<string, React.CSSProperties> = {
  shell: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    background: T.bg,
  },
  systemBar: {
    background: T.ink,
    color: 'rgba(255,255,255,0.55)',
    fontFamily: T.font.mono,
    fontSize: 11,
    letterSpacing: '0.04em',
    padding: '8px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  systemDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: T.accent,
    boxShadow: `0 0 8px ${T.accent}`,
    animation: 'livepulse 2s ease-in-out infinite',
  },
  root: {
    display: 'grid',
    gridTemplateColumns: '260px 1fr',
    flex: 1,
    background: T.bg,
  },
  sidebar: {
    background: T.bgSoft,
    borderRight: `1px solid ${T.rule}`,
    padding: '32px 0',
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: 0,
    height: '100vh',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontFamily: T.font.display,
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: '-0.025em',
    textTransform: 'lowercase',
    color: T.ink,
    padding: '0 24px 24px',
    borderBottom: `1px solid ${T.rule}`,
  },
  brandAccent: {
    fontFamily: T.font.display,
    fontStyle: 'normal',
    fontWeight: 700,
    letterSpacing: '-0.025em',
    textTransform: 'lowercase',
    color: T.inkMute,
    fontSize: 18,
  },
  nav: {
    flex: 1,
    padding: '24px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 28px',
    color: T.inkSoft,
    fontSize: 13,
    letterSpacing: '0.06em',
    transition: 'color 0.2s, background 0.2s',
    borderLeft: '2px solid transparent',
  },
  navLinkActive: {
    color: T.ink,
    background: T.accentSoft,
    borderLeft: `2px solid ${T.accent}`,
  },
  navLetter: {
    fontFamily: T.font.display,
    color: T.accent,
    fontSize: 14,
    width: 20,
    textAlign: 'center' as const,
  },
  sidebarFoot: {
    padding: '24px 28px 8px',
    borderTop: `1px solid ${T.rule}`,
  },
  foot: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 11,
    color: T.inkMute,
    letterSpacing: '0.04em',
  },
  main: {
    background: T.bg,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  topbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 40px',
    borderBottom: `1px solid ${T.rule}`,
    background: T.bg,
  },
  eyebrow: {
    fontSize: 11,
    letterSpacing: '0.32em',
    textTransform: 'uppercase' as const,
    color: T.inkSoft,
  },
  topbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  opStatus: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 12,
    color: T.live,
    letterSpacing: '0.04em',
  },
  content: {
    flex: 1,
    padding: '40px',
    maxWidth: 1280,
    width: '100%',
  },
};
